import { deepSetObject, digObject } from '@domain-group/javascript-utils'

import useAttachments from '@hooks/useAttachments'
import useBrand from '@hooks/useBrand'

import handleCallbackAction from '@functions/handleCallbackAction'

import type { UseFormPayload } from '@domain-group/react-hooks'
import type { BrandModel } from '@models/types'

interface UpdateBrandMediaReviewerSettingsParams {
  brandSettingKey: 'show_inspections' | 'use_light_text',
  entityState: UseFormPayload['entityState'],
  setEntityState: UseFormPayload['setEntityState'],
  updateBrandFn:(brandParams: BrandModel) => void,
}

const updateBrandMediaReviewerToggles = (params: UpdateBrandMediaReviewerSettingsParams) => {
  const {
    brandSettingKey, entityState, setEntityState, updateBrandFn,
  } = params

  const updatedState = deepSetObject(
    entityState,
    `options.branded_public_links.${brandSettingKey}`,
    !digObject(entityState, `options.branded_public_links.${brandSettingKey}`, true),
  )

  // @ts-expect-error TS2345
  setEntityState(updatedState)

  const updateBrandMediaReviewerSettingsPayload = {
    callbacks: {
      action: updateBrandFn,
    },
    entityParams: updatedState,
    toastText: 'Media Reviewer Branding Updated',
  }

  // @ts-expect-error TS2345
  return handleCallbackAction(updateBrandMediaReviewerSettingsPayload)
}

interface UseBrandEditMediaReviewerParams {
  brandId: number,
}

function useBrandEditMediaReviewer(params: UseBrandEditMediaReviewerParams) {
  const { brandId } = params

  const useBrandPayload = useBrand({
    id: brandId,
  }, {
    performHttpRequests: !!brandId,
    requestOptions: {
      include_attachment_file: true,
      include_brand_public_link_cover_image: true,
      include_brand_public_link_logo: true,
    },
  })
  const {
    brand,
    callbacks: {
      updateBrand: updateBrandFn,
    },
  } = useBrandPayload

  const attachmentsPayload = useAttachments({
    filters: {
      attachable_id: brandId,
      attachable_type: 'Brand',
    },
  })
  const { filteredAttachments } = attachmentsPayload

  const publicLinkLogo = filteredAttachments.filter(
    attachment => attachment.attachment_type === 'public_link_logo',
  )[0] || { attachable_id: brandId, attachable_type: 'Brand', attachment_type: 'public_link_logo' }

  const publicLinkCoverImage = filteredAttachments.filter(
    attachment => attachment.attachment_type === 'public_link_cover_image',
  )[0] || { attachable_id: brandId, attachable_type: 'Brand', attachment_type: 'public_link_cover_image' }

  const publicLinkCoverImageFileName = digObject(publicLinkCoverImage, 'file_file_name', '')
  const publicLinkCoverImageUrl = digObject(publicLinkCoverImage, 'links.original_url', '')

  const publicLinkLogoFileName = digObject(publicLinkLogo, 'file_file_name', '')
  const publicLinkLogoUrl = digObject(publicLinkLogo, 'links.original_url', '')

  const brandShowInspections = digObject(brand, 'options.branded_public_links.show_inspections', true)
  const brandUseLightText = digObject(brand, 'options.branded_public_links.use_light_text', true)

  const brandAccentColour = digObject(brand, 'options.branded_public_links.text_accent_colour', '')
  const isValidHex = /^([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(brandAccentColour.trim())

  const brandedMediaReviewerCheckboxItems = [
    {
      title: 'Accent colour',
      key: 'text_accent_colour',
      value: digObject(brand, 'options.branded_public_links.text_accent_colour', '').length > 0,
    },
    {
      title: 'Call to actions text colour',
      key: 'use_light_text',
      value: brandUseLightText,
    },
    {
      title: 'Display inspection times',
      key: 'show_inspections',
      value: brandShowInspections,
    },
    {
      title: 'General disclaimer',
      key: 'general_disclaimer',
      value: digObject(brand, 'options.branded_public_links.general_disclaimer', '').trim().length > 0,
    },
    {
      title: 'Legal disclaimer',
      key: 'legal_disclaimer',
      value: digObject(brand, 'options.branded_public_links.legal_disclaimer', '').trim().length > 0,
    },
  ]

  return {
    brandedMediaReviewerCheckboxItems,
    brandAccentColour,
    callbacks: {
      updateBrandMediaReviewerToggles: (
        brandSettingKey: 'show_inspections' | 'use_light_text',
        entityState: UseFormPayload['entityState'],
        setEntityState: UseFormPayload['setEntityState'],
      ) => updateBrandMediaReviewerToggles({
        brandSettingKey,
        entityState,
        setEntityState,
        updateBrandFn,
      }),
    },
    isValidHex,
    publicLinkCoverImage,
    publicLinkCoverImageFileName,
    publicLinkCoverImageUrl,
    publicLinkLogo,
    publicLinkLogoFileName,
    publicLinkLogoUrl,
  }
}
export default useBrandEditMediaReviewer
